import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { ROLES, type UserRole } from '@@auth/constants';
import config from '@@config';
import { type DevToolbarMode, MODE_SMALL } from '@@containers/DeveloperToolbar/constants';

const DEV_TOOLBAR_STORE = 'devToolbarStore';

export type DevToolbarState = {
    mode: DevToolbarMode;
    reducedUI: boolean;
    noFormRestoration: boolean;
    userRole: UserRole | null;
};

type DevToolbarActions = {
    setMode: (mode: DevToolbarMode) => void;
    setReducedUI: (reducedUI: boolean) => void;
    setNoFormRestoration: (noFormRestoration: boolean) => void;
    setUserRole: (userRole: UserRole | null) => void;
    clearDevToolbarState: () => void;
};

const initialState: DevToolbarState = {
    mode: MODE_SMALL,
    reducedUI: false,
    noFormRestoration: false,
    userRole: null,
};

export const useDevToolbarStore = create<DevToolbarState & DevToolbarActions>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,

                setMode: (mode) => set({ mode }, false, 'setMode'),
                setReducedUI: (reducedUI) => set({ reducedUI }, false, 'setReducedUI'),
                setNoFormRestoration: (noFormRestoration) =>
                    set({ noFormRestoration }, false, 'setNoFormRestoration'),
                setUserRole: (userRole) => set({ userRole }, false, 'setUserRole'),
                clearDevToolbarState: () => set({ ...initialState }, false, 'clearDevToolbarState'),
            }),
            {
                name: DEV_TOOLBAR_STORE,
                version: 3,
                partialize: (state) => ({
                    mode: state.mode,
                    reducedUI: state.reducedUI,
                    noFormRestoration: state.noFormRestoration,
                    userRole: state.userRole,
                }),
            },
        ),
        { name: DEV_TOOLBAR_STORE, enabled: true },
    ),
);

export const selectReducedUI = (state: DevToolbarState) => {
    const value = state.reducedUI;

    if ((config.env === 'production' || config.env === 'prod') && !config.isVisualTest) {
        return true;
    }

    // If there is set a user setting, use that value
    if (value != null) {
        return value;
    }

    // If we are not on production and no setting is set, use the following default
    // value: UI will not be reduced for visual tests and development systems. For
    // other systems it is (for example: staging, dynamic)
    return !config.isVisualTest && config.env !== 'development';
};

export const selectMode = (state: DevToolbarState) => {
    const value = state.mode;

    if (config.devToolbarDisabled || config.isVisualTest) {
        return null;
    }

    // If there is a value mode, return it
    if (value != null) {
        return value;
    }

    // On all systems, but visual tests, the devToolbar is shown
    // by default
    if (!config.isVisualTest) {
        return MODE_SMALL;
    }

    return null;
};

export const selectNoFormRestoration = (state: DevToolbarState) => {
    const value = state.noFormRestoration;

    if (config.isVisualTest) {
        if (value != null) {
            return value;
        }

        return true;
    }

    return value;
};

export const selectSmartUserRole = (state: DevToolbarState) => {
    // If we are on production while not running visual tests, skip special treatment.
    // If we are not on production, or running visual tests in production mode, special
    // treatment is possible

    const value = state.userRole;

    // TODO there is something wrong with these ifs. Conditions overlaps etc.
    if (config.env !== 'production' || config.isVisualTest) {
        // If there is set a user setting, use that value
        if (value != null) {
            return value;
        }

        // If we are running visual tests or we are on development, we always will be
        // admins
        if (config.isVisualTest || config.env === 'development') {
            return ROLES.ADMIN;
        }
    }

    // If there was no special rule applied, we return null.
    // Use a fallback to the authSlice in your selector
    // State between slices shouldn't be accessed and/or shared
    return null;
};

export const useReducedUI = () => useDevToolbarStore(selectReducedUI);
export const useDevToolbarMode = () => useDevToolbarStore(selectMode);
export const useNoFormRestoration = () => useDevToolbarStore(selectNoFormRestoration);
export const useDevToolbarSmartUserRole = () => useDevToolbarStore(selectSmartUserRole);
